import { LRUCache } from "lru-cache";

import { buildAPIUrl, fetchFunctionResult, roundNumbersTo2DP } from "../helpers/utils";
import { API_END_POINTS } from "../helpers/api-paths";

const CACHE = new LRUCache<string, Promise<any>>({
  max: 200,
});

function generateCacheKey(
  params: Record<string, any>,
  callback?: (params: Record<string, any>) => Record<string, any>,
): string {
  if (callback) params = callback(params);
  const sortedEntries = Object.entries(params).sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(sortedEntries);
}

async function fetchResult(apiEndPoint: string, queryParams, invocation: CustomFunctions.Invocation) {
  const cacheKey = generateCacheKey(queryParams, roundNumbersTo2DP);
  let resultPromise: Promise<any>;

  if (CACHE.has(cacheKey)) {
    resultPromise = CACHE.get(cacheKey);
  } else {
    const url = buildAPIUrl(apiEndPoint, queryParams);
    resultPromise = await fetchFunctionResult(url, invocation);
    CACHE.set(cacheKey, resultPromise);
  }
  return resultPromise;
}

/**
 * Returns the AASHTO classification of the soil.
 * @customfunction AASHTO
 * @param liquid_limit     Liquid limit of the soil (%). liquid_limit >= 0 and liquid_limit > plastic_limit
 * @param plastic_limit    Plastic limit of the soil (%). plastic_limit >= 0.
 * @param fines            Percentage of fines in the soil. fines >= 0.
 * @param only_group_idx   Indicates whether only the group index should be returned. It takes precedence
 *                         over add_group_idx and description. If omitted, only_group_idx = false.
 *  @param add_group_idx   Indicates whether the group index should be added to the
 *                         classification.  If omitted, add_group_idx = true.
 * @param description      Indicates whether to return the description of the
 *                         classification. If omitted, description = false. NB: If only_group_idx=true,then
 *                         description won't be returned, intead the group index will be returned.
 * @param invocation       Custom function invocation object.
 * @returns {Promise<string>} AASHTO classification of the soil.
 * @requiresAddress
 * @helpurl https://docs.geolysis.io/en/latest/excel/functions/aashto
 */
export async function aashto(
  liquid_limit: number,
  plastic_limit: number,
  fines: number,
  only_group_idx?: boolean,
  add_group_idx?: boolean,
  description?: boolean,
  invocation?: CustomFunctions.Invocation,
): Promise<string> {
  only_group_idx = only_group_idx ?? false;
  add_group_idx = add_group_idx ?? true;
  description = description ?? false;

  const queryParams = { liquid_limit, plastic_limit, fines };
  const resultPromise = fetchResult(API_END_POINTS.AASHTO_CLASSIFICATION, queryParams, invocation);

  return resultPromise.then((res) => {
    if (only_group_idx) return res.group_index;
    else return description ? res.description : add_group_idx ? res.symbol : res.symbol_no_group_idx;
  });
}

/**
 * Returns the USCS classification of the soil.
 * @customfunction USCS
 * @param liquid_limit     Liquid limit of the soil (%). liquid_limit >= 0 and liquid_limit > plastic_limit
 * @param plastic_limit    Plastic limit of the soil (%). plastic_limit >= 0.
 * @param fines            Percentage of fines in the soil. fines >= 0.
 * @param sand             Percentage of sand in the soil. sand >= 0.
 * @param d_10             Diameter at which 10% of soil is finer.
 * @param d_30             Diameter at which 30% of soil is finer.
 * @param d_60             Diameter at which 60% of soil is finer.
 * @param organic          Indicates whether the soil is organic. If omitted,
 *                         organic = false.
 * @param description      Indicates whether to return the description of the
 *                         classification. If omitted, description = false.
 * @param invocation       Custom function invocation object.
 * @returns {Promise<string>} USCS classification of the soil.
 * @requiresAddress
 * @helpurl https://docs.geolysis.io/en/latest/excel/functions/uscs
 */
export async function uscs(
  liquid_limit: number,
  plastic_limit: number,
  fines: number,
  sand: number,
  d_10?: number,
  d_30?: number,
  d_60?: number,
  organic?: boolean,
  description?: boolean,
  invocation?: CustomFunctions.Invocation,
): Promise<string> {
  organic = organic ?? false;
  description = description ?? false;

  const queryParams = { liquid_limit, plastic_limit, fines, sand, d_10, d_30, d_60, organic };
  const resultPromise = fetchResult(API_END_POINTS.USCS_CLASSIFICATION, queryParams, invocation);
  return resultPromise.then((res) => (description ? res.description : res.symbol));
}

/**
 * Returns the ultimate bearing capacity of the soil for shallow foundations (kN/m^2 or kPa).
 * @customfunction ULTIMATE_BEARING_CAPACITY
 * @param friction_angle           Internal angle of friction for general shear failure (degrees).
 *                                 friction_angle >= 0.
 * @param cohesion                 Cohesion of the soil (kN/m^2). cohesion >= 0.
 * @param moist_unit_wgt           Unit weight of the soil (kN/m^3). moist_unit_wgt > 0.
 * @param depth                    Depth of the foundation (m).
 * @param width                    Width of the foundation footing (m). width > 0.0
 * @param length                   Length of the foundation footing (m). length > 0.0
 * @param factor_of_safety         Factor of safety. If omitted factor_of_safety = 3.
 * @param saturated_unit_wgt       Saturated unit weight of soil (kN/m^3). If omitted, saturated_unit_wgt = 20.5,
 * @param ground_water_level       Depth of the ground water level (m). ground_water_level >= 0.
 * @param eccentricity             Eccentricity of applied load (m). eccentricity >= 0, If omitted,
 *                                 eccentricity = 0.0.
 * @param load_angle               Inclination of applied load with the vertical (degrees). If omitted,
 *                                 load_angle = 0.0.
 * @param apply_local_shear        Indicates whether to apply local shear. If omitted,
 *                                 apply_local_shear = false.
 * @param shape                    Shape of the foundation footing. If omitted, shape = "square".
 *                                 Available options are "square", "rectangle", "circle", and "strip".
 * @param ubc_type                 Type of bearing capacity calculation. If omitted, ubc_type = "vesic".
 *                                 Available options are "terzaghi", and "vesic".
 * @param allowable_bearing_capacity Indicates whether to return the allowable bearing capacity of the soil or not.
 * @param invocation               Custom function invocation object.
 * @returns {Promise<number>} Ultimate bearing capacity of the soil.
 * @requiresAddress
 * @helpurl https://docs.geolysis.io/en/latest/excel/functions/ultimate-bearing-capacity
 */
export async function ultimateBearingCapacity(
  friction_angle: number,
  cohesion: number,
  moist_unit_wgt: number,
  depth: number,
  width: number,
  length?: number,
  factor_of_safety?: number,
  saturated_unit_wgt?: number,
  ground_water_level?: number,
  eccentricity?: number,
  load_angle?: number,
  apply_local_shear?: boolean,
  shape?: string,
  ubc_type?: string,
  allowable_bearing_capacity?: boolean,
  invocation?: CustomFunctions.Invocation,
): Promise<number> {
  factor_of_safety = factor_of_safety ?? 3;
  saturated_unit_wgt = saturated_unit_wgt ?? 20.5;
  apply_local_shear = apply_local_shear ?? false;
  shape = shape ?? "square";
  ubc_type = ubc_type ?? "vesic";
  allowable_bearing_capacity = allowable_bearing_capacity ?? false;

  const queryParams = {
    friction_angle,
    cohesion,
    moist_unit_wgt,
    depth,
    width,
    length,
    factor_of_safety,
    saturated_unit_wgt,
    ground_water_level,
    eccentricity,
    load_angle,
    shape,
    apply_local_shear,
    ubc_type,
  };

  const resultPromise = fetchResult(API_END_POINTS.ULTIMATE_BEARING_CAPACITY, queryParams, invocation);
  return resultPromise.then((res) =>
    allowable_bearing_capacity ? res.allowable_bearing_capacity : res.ultimate_bearing_capacity,
  );
}

/**
 * Returns the allowable bearing capacity of the soil for shallow foundations (kN/m^2 or kPa).
 * @customfunction ALLOWABLE_BEARING_CAPACITY
 * @param corrected_spt_n_value     Corrected SPT N values. Valid values for
 *                                  each corrected_spt_n_value should be between
 *                                  (0 < corrected_spt_n_value <= 100)
 * @param tol_settlement            Tolerable settlement of foundation (mm). (0 <= tol_settlement <= 25.4)
 * @param depth                     Depth of the foundation (m). depth > 0.0
 * @param width                     Width of the foundation footing (m). width > 0.0
 * @param length                    Length of the foundation footing (m). length > 0.
 * @param eccentricity              Eccentricity of applied load (m). eccentricity >= 0. If omitted,
 *                                  eccentricity = 0.0.
 * @param ground_water_level        Depth of the ground water level (m). ground_water_level >= 0.0
 * @param shape                     Shape of the foundation footing.  If omitted, shape = "square".
 *                                  Available options are "square", "rectangle", "circle", and "strip".
 * @param foundation_type           Type of foundation. If omitted, foundation_type = "pad".
 *                                  Available options are "pad" and "mat".
 * @param abc_type                  Type of bearing capacity calculation. If omitted, abc_type = "bowles".
 *                                  Available options are "bowles", "meyerhof", and "terzaghi".
 * @param invocation                Custom function invocation object.
 * @returns {Promise<number>} Allowable bearing capacity of the soil.
 * @requiresAddress
 * @helpurl https://docs.geolysis.io/en/latest/excel/functions/allowable-bearing-capacity
 */
export async function allowableBearingCapacity(
  corrected_spt_n_value: number,
  tol_settlement: number,
  depth: number,
  width: number,
  length?: number,
  eccentricity?: number,
  ground_water_level?: number,
  shape?: string,
  foundation_type?: string,
  abc_type?: string,
  invocation?: CustomFunctions.Invocation,
): Promise<number> {
  shape = shape ?? "square";
  foundation_type = foundation_type ?? "pad";
  abc_type = abc_type ?? "bowles";

  const queryParams = {
    corrected_spt_n_value,
    tol_settlement,
    depth,
    width,
    length,
    eccentricity,
    ground_water_level,
    shape,
    foundation_type,
    abc_type,
  };
  const resultPromise = fetchResult(API_END_POINTS.ALLOWABLE_BEARING_CAPACITY, queryParams, invocation);
  return resultPromise.then((res) => res.allowable_bearing_capacity);
}

/**
 * SPT N deisgn calculation using weighted average.
 * @customfunction SPT_N_DESIGN
 * @param corrected_spt_n_values   Corrected SPT N values. Valid value for each corrected_spt_n_value
 *                                 should be between (0 < corrected_spt_n_value <= 100)
 * @param invocation               Custom function invocation object.
 * @returns {Promise<number>}      SPT N design value.
 * @requiresAddress
 * @helpurl https://docs.geolysis.io/en/latest/excel/functions/spt-n-design
 */
export async function spt_n_design(
  corrected_spt_n_values: number[][][],
  invocation?: CustomFunctions.Invocation,
): Promise<number> {
  const queryParams = {
    corrected_spt_n_values: corrected_spt_n_values.flat(2),
    method: "wgt",
  };
  const resultPromise = fetchResult(API_END_POINTS.SPT_N_DESIGN, queryParams, invocation);
  return resultPromise.then((res) => res.spt_n_design);
}

/**
 * Returns the SPT N-value corrected for field procedures.
 * @customfunction SPT_ENERGY_CORRECTION
 * @param recorded_spt_n_value  Recorded SPT N value. Valid values are between
 *                              (0 < recorded_spt_n_value <= 100)
 * @param energy_percentage     Percentage of energy reaching the tip of the sampler. Valid values
 *                              are between (0.0 < energy_percentage <= 1.0). If omitted,
 *                              energy_percentage = 0.6.
 * @param borehole_diameter     Borehole diameter (mm). Valid values are between
 *                              (65.0 <= borehole_diameter <= 200.0) If omitted, borehole_diameter = 65.0.
 * @param rod_length            Length of spt rod (m). Valid values are (rod_length > 0.0). If omitted,
 *                              rod_length = 3.0.
 * @param hammer_type           Hammer type. If omitted, hammer_type = "donut_1".
 *                              Available options are "automatic", "donut_1", "donut_2", "safety",
 *                              "drop" and "pin"
 * @param sampler_type          Sampler type. If omitted, sampler_type = "standard".
 *                              Available options are "standard" and "non_standard".
 * @param invocation            Custom function invocation object.
 * @returns {Promise<number>}   SPT N-value corrected for field procedures.
 * @requiresAddress
 * @helpurl https://docs.geolysis.io/en/latest/excel/functions/spt-energy-correction
 */
export async function energyCorrection(
  recorded_spt_n_value: number,
  energy_percentage?: number,
  borehole_diameter?: number,
  rod_length?: number,
  hammer_type?: string,
  sampler_type?: string,
  invocation?: CustomFunctions.Invocation,
): Promise<number> {
  energy_percentage = energy_percentage ?? 0.6;
  borehole_diameter = borehole_diameter ?? 65.0;
  rod_length = rod_length ?? 3.0;
  hammer_type = hammer_type ?? "donut_1";
  sampler_type = sampler_type ?? "standard";

  const queryParams = {
    recorded_spt_n_value,
    energy_percentage,
    borehole_diameter,
    rod_length,
    hammer_type,
    sampler_type,
  };

  const resultPromise = fetchResult(API_END_POINTS.ENERGY_CORRECTION, queryParams, invocation);
  return resultPromise.then((res) => res.standardized_spt_n_value);
}

/**
 * Returns the SPT N-value corrected for overburden pressure.
 * @customfunction SPT_OVERBURDEN_PRESSURE_CORRECTION
 * @param std_spt_n_value Standardized SPT N value. Valid values are between
 *                        (0.0 < std_spt_n_value <= 100.0)
 * @param eop             Effective overburden pressure (kN/m^2).
 * @param opc_type        Overburden pressure correction type. If omitted, opc_type = "gibbs".
 *                        Available options are "gibbs", "bazaraa", "peck", "liao", and "skempton".
 * @param invocation      Custom function invocation object.
 * @returns {Promise<number>} Overburden pressure SPT N-value correction.
 * @requiresAddress
 * @helpurl https://docs.geolysis.io/en/latest/excel/functions/spt-overburden-pressure-correction
 */
export async function overburdenPressureCorrection(
  std_spt_n_value: number,
  eop: number,
  opc_type?: string,
  invocation?: CustomFunctions.Invocation,
): Promise<number> {
  opc_type = opc_type ?? "gibbs";

  const queryParams = {
    std_spt_n_value,
    eop,
    opc_type,
  };

  const resultPromise = fetchResult(API_END_POINTS.OVERBURDEN_PRESSURE_CORRECTION, queryParams, invocation);
  return resultPromise.then((res) => res.corrected_spt_n_value);
}

/**
 * Returns the SPT N-value corrected for dilatancy.
 * @customfunction SPT_DILATANCY_CORRECTION
 * @param corrected_spt_n_value Corrected SPT N value. Valid values are between
 *                              (0.0 < corrected_spt_n_value <= 100.0)
 * @param invocation            Custom function invocation object.
 * @returns {Promise<number>} Dilatancy SPT N-value correction.
 * @requiresAddress
 * @helpurl https://docs.geolysis.io/en/latest/excel/functions/spt-dilatancy-correction
 */
export async function dilatancyCorrection(
  corrected_spt_n_value: number,
  invocation?: CustomFunctions.Invocation,
): Promise<number> {
  const queryParams = { corr_spt_n_value: corrected_spt_n_value };
  const resultPromise = fetchResult(API_END_POINTS.DILATANCY_CORRECTION, queryParams, invocation);
  return resultPromise.then((res) => res.corrected_spt_n_value);
}

CustomFunctions.associate("AASHTO", aashto);
CustomFunctions.associate("USCS", uscs);
CustomFunctions.associate("ULTIMATE_BEARING_CAPACITY", ultimateBearingCapacity);
CustomFunctions.associate("ALLOWABLE_BEARING_CAPACITY", allowableBearingCapacity);
CustomFunctions.associate("SPT_N_DESIGN", spt_n_design);
CustomFunctions.associate("SPT_ENERGY_CORRECTION", energyCorrection);
CustomFunctions.associate("SPT_OVERBURDEN_PRESSURE_CORRECTION", overburdenPressureCorrection);
CustomFunctions.associate("SPT_DILATANCY_CORRECTION", dilatancyCorrection);